import {
  Dialog,
  makeStyles,
  styled,
  Theme,
  Typography,
} from '@material-ui/core';
import React from 'react';
import colorPalette from '../themes/colorPalette';
import ClearIcon from '@material-ui/icons/Clear';
import PropTypes from 'prop-types';
import useGetBrowserHeight from '../hooks/useGetBrowserHeight';
import { ReactNode } from 'react';

const { black, white } = colorPalette;

type ModalProps = PropTypes.InferProps<typeof propTypes> & typeof defaultProps;

const StyledCloseIcon = styled(ClearIcon)(() => ({
  position: 'absolute',
  top: '8px',
  right: '8px',
  cursor: 'pointer',
  color: black,
  backgroundColor: white,
}));

export const ModalOverlayTitle = ({ children }: { children: ReactNode }) => (
  <Typography variant="subtitle1" style={{ marginBottom: '16px' }}>
    {children}
  </Typography>
);
export const ModalOverlayBody = ({ children }: { children: ReactNode }) => (
  <Typography variant="subtitle2">{children}</Typography>
);
const modalDefaultHeightMargin = 64;

interface StylesProps {
  maxWidth: number;
  browserHeight: number;
}

const useStyles = makeStyles<Theme, StylesProps>(theme => ({
  paper: {
    border: '3px solid rgba(0, 0, 0, 0.0)',
    width: '100%',
    maxWidth: ({ maxWidth }) => `${maxWidth + 40}px`,
    borderRadius: '20px',
    minHeight: '20px',
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.3)',
    padding: '40px 20px',
    margin: '20px',
    textAlign: 'center',
    maxHeight: '760px',
    [theme.breakpoints.up('sm')]: {
      padding: '40px',
    },
  },
  paperScrollPaper: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: ({ browserHeight }) =>
      browserHeight > 760 + modalDefaultHeightMargin
        ? '760px'
        : `calc(100% - ${modalDefaultHeightMargin}px)`,
  },
}));

function ModalOverlay({
  children,
  onClose,
  maxWidth,
  open,
  className,
  ...other
}: ModalProps) {
  const browserHeight = useGetBrowserHeight();
  const { paper, paperScrollPaper } = useStyles({ maxWidth, browserHeight });
  return (
    <Dialog
      onClose={onClose}
      open={open}
      {...other}
      classes={{ paper: `${paper} ${className || ''}`, paperScrollPaper }}
    >
      <StyledCloseIcon onClick={onClose} aria-label="Close modal" />
      {children}
    </Dialog>
  );
}

const defaultProps = {
  maxWidth: 640,
};

const propTypes = {
  /** Custom content */
  children: PropTypes.node.isRequired,
  onClose: PropTypes.func.isRequired,
  maxWidth: PropTypes.number,
  open: PropTypes.bool.isRequired,
  className: PropTypes.string,
};

ModalOverlay.propTypes = propTypes;
ModalOverlay.defaultProps = defaultProps;

export default ModalOverlay;
